import React, { useState } from 'react';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { useNotesUIStore } from '../../../../store/notesUIStore';
import { NoteSearchInput } from './NoteSearchInput';
import { NoteUserFilterDropdown } from './NoteUserFilterDropdown';
import { cn } from '../../../../lib/utils';

/**
 * NoteFilterToolbar component
 *
 * Provides filtering controls for notes, including search and user filters.
 * Designed to match the styling of TaskFilterToolbar for consistency.
 */
interface NoteFilterToolbarProps {
  isMobile: boolean;
  userFilters: UserFilter[];
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
  hasCollaborators: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export default function NoteFilterToolbar({
  isMobile,
  userFilters,
  toggleUserFilter,
  hasCollaborators,
  searchQuery,
  onSearchChange,
}: NoteFilterToolbarProps) {
  const { activeFilters, resetFilters, setSearchQuery } = useNotesUIStore();
  const [isUserFilterOpen, setIsUserFilterOpen] = useState(false);

  // Check if any filters are active
  const hasActiveFilters =
    (searchQuery || activeFilters.searchQuery || '').trim() !== '' ||
    activeFilters.userIds.length > 0;

  // Handler for toggling user filter dropdown
  const handleToggleUserFilter = () => {
    setIsUserFilterOpen(!isUserFilterOpen);
  };

  return (
    <div
      className={cn(
        'w-full flex items-center py-3 md:py-4 px-1 md:px-5',
        isMobile ? 'justify-center' : 'justify-end' // Center on mobile, end-aligned on desktop
      )}
    >
      {/* Filter Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search Input - Only show on desktop */}
        {!isMobile && (
          <NoteSearchInput
            className="mr-1"
            isMobile={isMobile}
            value={searchQuery}
            onChange={onSearchChange || setSearchQuery}
          />
        )}

        {/* User filter (only show when there are collaborators and project is shared) */}
        {hasCollaborators && userFilters.length > 0 && (
          <NoteUserFilterDropdown
            filters={userFilters}
            onToggleFilter={toggleUserFilter}
            isOpen={isUserFilterOpen}
            onToggle={handleToggleUserFilter}
          />
        )}

        {/* Reset filters button (only show when there are active filters) */}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="px-3 py-1.5 rounded-full text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            aria-label="Reset filters"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
